import { useEffect, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../contexts/useAuth';

interface UserMenuProps {
    open: boolean;
    onClose: () => void;
    /**
     * The boundary the outside-click check treats as "inside" — must wrap both the trigger (the
     * sidebar's user row) and this menu, exactly like NotificationsPopover's boundaryRef wraps the
     * bell and its popover. Without this, a click on the trigger bubbles a `mousedown` this
     * component sees as "outside" (its own DOM node doesn't contain the trigger) and closes on it
     * — then the trigger's own click handler re-opens it a beat later, so the trigger can never
     * close an open menu. See NotificationsPopover's identical prop for the full story.
     */
    boundaryRef: RefObject<HTMLElement | null>;
}

/**
 * The prototype's account menu, anchored bottom-left over the sidebar's user row.
 *
 * Unmounted entirely while closed (`open` false renders null), same as NotificationsPopover and
 * for the same reason: there is no transition to preserve, so nothing needs `inert` — an unmounted
 * menu is already out of the tab order and the accessibility tree.
 */
export default function UserMenu({ open, onClose, boundaryRef }: UserMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;

        // Same focus contract as NotificationsPopover/MoreSheet: capture whatever had focus (the
        // sidebar's user row, in practice) before moving focus into the menu, and hand it back on
        // close.
        previouslyFocused.current = document.activeElement as HTMLElement | null;
        menuRef.current?.focus();

        return () => {
            previouslyFocused.current?.focus();
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }

        function handleClickOutside(e: MouseEvent) {
            // Checked against boundaryRef (trigger + menu), not menuRef (menu only) — see the
            // prop's doc comment for why that distinction is the whole fix.
            if (boundaryRef.current && !boundaryRef.current.contains(e.target as Node)) {
                onClose();
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose, boundaryRef]);

    if (!open) return null;

    const handleAccountSettings = () => {
        navigate('/profile');
        onClose();
    };

    const handleLogout = async () => {
        // Same flow as legacy UserDropdown: await the server logout before navigating away, so
        // the signin route never renders against a still-authenticated session.
        await logout();
        navigate('/login');
        onClose();
    };

    return (
        <div
            ref={menuRef}
            role="menu"
            aria-label="Account menu"
            tabIndex={-1}
            data-testid="user-menu"
            className="fixed left-4 bottom-24 w-[220px] bg-surface border border-border rounded-[14px] shadow-[0_16px_40px_rgba(28,33,28,.25)] p-2 z-60"
        >
            <div className="px-3 py-2.5 border-b border-border-inner mb-1.5">
                <div className="text-[13.5px] font-bold text-ink">
                    {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-ink-muted">{user?.email}</div>
            </div>

            <button
                type="button"
                role="menuitem"
                onClick={handleAccountSettings}
                className="block w-full text-left px-3 py-[9px] rounded-[9px] text-[13.5px] font-semibold text-ink"
            >
                Account settings
            </button>

            {/* Business settings used to live here. It moved to the sidebar's foot: it is a
                property of the BUSINESS, not of the person signed in, and it was hard to find
                behind an avatar. This menu is now only about the signed-in user. */}

            <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                // The token set has no destructive colour — FreeTrialBanner sets the precedent for
                // reaching straight for a Tailwind palette colour when the tokens genuinely don't
                // cover a semantic. text-red-700 is the closest match to the prototype's #b91c1c.
                className="block w-full text-left px-3 py-[9px] rounded-[9px] text-[13.5px] font-semibold text-red-700 dark:text-red-400 border-t border-border-inner mt-1.5"
            >
                Log out
            </button>
        </div>
    );
}
